import { useMemo, useRef, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { getMatterAppLogs, VaultKeys } from '../../../api/vault-api';
import AvatarChip from '../../../shared/avatar-chip';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import LogDetailsDialog from '../../logging/log-details-dialog';
import { AppLog } from '../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import Alert from '@mui/material/Alert';
import FindInPage from '@mui/icons-material/FindInPage';

interface MatterLogHistoryProps {
  matterId: string;
}

const pageSize = 100;

function decodeLogText(value: string | null | undefined) {
  if (!value) {
    return '';
  }
  return value
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

export default function MatterLogHistory({
  matterId,
}: Readonly<MatterLogHistoryProps>) {
  const { administrationRoles } = useUserSettings();
  const canViewLogs = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.ViewAppLogs.value
  );
  const [selectedLog, setSelectedLog] = useState<AppLog | null>(null);
  const [openLogDetails, setOpenLogDetails] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize,
    cursor: '',
  });
  const pageCursors = useRef<Record<number, string>>({ 0: '' });

  const columns: GridColDef<AppLog>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        editable: false,
        sortable: true,
        filterable: true,
        minWidth: 170,
        type: 'dateTime',
        valueGetter: (_value, row) => new Date(row.createdDate),
        valueFormatter: (value) => {
          if (!value) {
            return '';
          }
          return new Date(value as string).toLocaleString();
        },
      },
      {
        field: 'performingUser',
        headerName: 'Performing User',
        editable: false,
        sortable: true,
        filterable: true,
        minWidth: 170,
        renderCell: (params) => {
          if (params.value) {
            return <AvatarChip email={params.value} type="User" />;
          }
        },
      },
      {
        field: 'permission',
        headerName: 'Permission',
        editable: false,
        sortable: true,
        filterable: true,
        minWidth: 200,
        valueGetter: (_value, row) => {
          if (row.permissionNiceName && row.permissionNiceName !== '') {
            return row.permissionNiceName;
          } else if (row.permission) {
            return row.permission;
          } else {
            return 'No Permission';
          }
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        editable: false,
        sortable: false,
        filterable: true,
        display: 'flex',
        flex: 1,
        valueGetter: (_value, row) => {
          if (row.action) {
            return row.action;
          } else {
            return 'No action was performed.';
          }
        },
      },
      {
        field: 'target',
        headerName: 'Target',
        editable: false,
        sortable: true,
        filterable: true,
        minWidth: 200,
        valueGetter: (_value, row) => {
          if (row.target) {
            return row.target;
          } else {
            return 'No Target';
          }
        },
      },
    ],
    []
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [VaultKeys.MatterLogs, matterId, paginationModel.cursor],
    queryFn: () => getMatterAppLogs(matterId, paginationModel.cursor),
    enabled: canViewLogs && !!matterId,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue getting app logs for this matter. Please refresh and try again.',
    },
  });

  const rows = useMemo(
    () =>
      (data?.results ?? []).map((log) => ({
        ...log,
        id: uuidv4(),
        action: decodeLogText(log.action),
        permission: decodeLogText(log.permission),
      })),
    [data?.results]
  );

  if (!canViewLogs) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        The &quot;View App Logs&quot; permission is required to view App Logs.
      </Alert>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <SharedDataGridServer
          data={data}
          rows={rows}
          columns={columns}
          loading={isLoading || isFetching}
          showToolbar={false}
          sortingMode="client"
          filterMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel: GridPaginationModel) => {
            if (newModel.page === paginationModel.page) {
              return;
            }
            const cursor =
              newModel.page > paginationModel.page
                ? data?.cursor || ''
                : pageCursors.current[newModel.page] ?? '';
            if (newModel.page > paginationModel.page) {
              pageCursors.current[newModel.page] = cursor;
            }
            setPaginationModel({
              page: newModel.page,
              pageSize,
              cursor,
            });
          }}
          pageSizeOptions={[pageSize]}
          disableRowSelectionOnClick
          onRowClick={(row) => {
            const log = rows.find((item) => item.id === row.row.id);
            if (!log) {
              return;
            }
            setSelectedLog(log);
            setOpenLogDetails(true);
          }}
          slots={{
            ...createGridOverlays(
              'Logs',
              <FindInPage />,
              'No logs found for this matter.',
              ' Try changing your search term.'
            ),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'linear-progress',
            },
          }}
          sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        />
      </div>
      {selectedLog && openLogDetails ? (
        <LogDetailsDialog
          open={openLogDetails}
          handleClose={() => setOpenLogDetails(false)}
          selectedLog={selectedLog}
        />
      ) : null}
    </>
  );
}
